import { FormControlLabel, Stack, Switch } from "@mui/material";
import Typography from "@mui/material/Typography";
import ConfirmationModal from "common/components/ConfirmationModal";
import EntaAccordion from "common/components/EntaAccordion";
import { PanelSections } from "developer/edit/constants";
import useDynamicField from "developer/edit/hooks/useDynamicField";
import useIsExpanded from "developer/edit/hooks/useIsExpanded";
import React, { useCallback, useEffect, useState } from "react";
import FieldOptionsAdvancedForm from "./FieldOptionsAdvancedForm";
import FieldOptionsBasicForm from "./FieldOptionsBasicForm";

export type SelectOption = {
  label?: string;
  value: string;
};

export type ChildSelectConfig = {
  dependsOn: string;
  childOptions: Record<string, SelectOption[]>;
};

const OptionsSection: React.FC = () => {
  const { isExpanded, handleChangeIsExpanded, isDefaultExpanded, hide } =
    useIsExpanded(PanelSections.Options);
  const {
    input: { value: selectOptions, onChange },
  } = useDynamicField<SelectOption[] | ChildSelectConfig | undefined>(
    "fieldDef.options"
  );
  const [isAdvancedOptionsEnabled, setIsAdvancedOptionsEnabled] =
    useState<boolean>(false);
  const [isShowLabelsEnabled, setIsShowLabelsEnabled] =
    useState<boolean>(false);
  const [confirmationToggleOptions, setConfirmationToggleOptions] =
    useState<boolean>(false);
  const [confirmationToggleSplitLabel, setConfirmationToggleSplitLabel] =
    useState<boolean>(false);
  const [keySelected, setKeySelected] = useState<string | null>(null);

  const handleConfirmationToggleOptions = useCallback(() => {
    let initialOptions: ChildSelectConfig | SelectOption[];

    if (!isAdvancedOptionsEnabled) {
      initialOptions = {
        dependsOn: "",
        childOptions: {},
      };
    } else {
      initialOptions = [];
    }
    onChange({ target: { value: initialOptions } });
    setIsAdvancedOptionsEnabled(!isAdvancedOptionsEnabled);
    setConfirmationToggleOptions(false);
  }, [onChange, isAdvancedOptionsEnabled]);

  const handleChangeAdvancedOptionsEnabled = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      if (!confirmationToggleOptions) setConfirmationToggleOptions(true);
    },
    [confirmationToggleOptions]
  );

  const isBasicSelectOptions = useCallback(
    (
      selectOptions: SelectOption[] | ChildSelectConfig | undefined
    ): selectOptions is SelectOption[] => {
      return (
        Array.isArray(selectOptions) || typeof selectOptions === "undefined"
      );
    },
    []
  );

  const isAdvancedSelectOptions = useCallback(
    (
      selectOptions: SelectOption[] | ChildSelectConfig | undefined
    ): selectOptions is ChildSelectConfig => {
      return (
        typeof selectOptions === "object" &&
        "dependsOn" in selectOptions &&
        "childOptions" in selectOptions
      );
    },
    []
  );

  const hasOptionsWithLabel = useCallback(() => {
    return (
      (isBasicSelectOptions(selectOptions) &&
        selectOptions.some((option) => option.label)) ||
      (isAdvancedSelectOptions(selectOptions) &&
        keySelected &&
        selectOptions.childOptions[keySelected].some((option) => option.label))
    );
  }, [
    isAdvancedSelectOptions,
    isBasicSelectOptions,
    keySelected,
    selectOptions,
  ]);

  const handleConfirmationToggleSplitLabel = useCallback(() => {
    if (isBasicSelectOptions(selectOptions)) {
      const newOptions: SelectOption[] = selectOptions.map(({ value }) => ({
        value,
      }));
      onChange({ target: { value: newOptions } });
    }

    if (isAdvancedSelectOptions(selectOptions) && !!keySelected) {
      const newSelectOptions = selectOptions.childOptions[keySelected].map(
        ({ value }) => ({ value })
      );

      const newOptions: ChildSelectConfig = {
        ...selectOptions,
        childOptions: {
          [keySelected]: newSelectOptions,
        },
      };

      onChange({ target: { value: newOptions } });
    }

    setConfirmationToggleSplitLabel(false);
    setIsShowLabelsEnabled(false);
  }, [
    isAdvancedSelectOptions,
    isBasicSelectOptions,
    keySelected,
    onChange,
    selectOptions,
  ]);

  const handleChangeShowLabel = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      const showConfirmationModal = !checked && hasOptionsWithLabel();

      if (showConfirmationModal) {
        setConfirmationToggleSplitLabel(true);
      } else {
        setIsShowLabelsEnabled(checked);
      }
    },
    [hasOptionsWithLabel]
  );

  const handleChange = useCallback(
    (newSelectedOptions: SelectOption[] | ChildSelectConfig) => {
      onChange({ target: { value: newSelectedOptions } });
    },
    [onChange]
  );

  if (isAdvancedSelectOptions(selectOptions) && !isAdvancedOptionsEnabled) {
    setIsAdvancedOptionsEnabled(true);
  }

  useEffect(() => {
    if (hasOptionsWithLabel()) {
      setIsShowLabelsEnabled(true);
    }
  }, [hasOptionsWithLabel]);

  const content: JSX.Element = isBasicSelectOptions(selectOptions) ? (
    <FieldOptionsBasicForm
      selectOptions={selectOptions}
      onChange={handleChange}
      isShowLabelsEnabled={isShowLabelsEnabled}
    />
  ) : isAdvancedSelectOptions(selectOptions) ? (
    <FieldOptionsAdvancedForm
      keySelected={keySelected}
      setKeySelected={setKeySelected}
      selectOptions={selectOptions}
      onChange={handleChange}
      isShowLabelsEnabled={isShowLabelsEnabled}
    />
  ) : (
    <></>
  );

  if (hide) return null;

  return (
    <EntaAccordion
      title="Options"
      expanded={isExpanded}
      onChange={handleChangeIsExpanded}
      defaultExpanded={isDefaultExpanded}
    >
      <Stack spacing={2}>
        {content}
        <Stack>
          <FormControlLabel
            label="Split Value/Label"
            control={
              <Switch
                color="primary"
                size="small"
                checked={isShowLabelsEnabled}
                onChange={handleChangeShowLabel}
              />
            }
          />
          <FormControlLabel
            label="Conditional Options"
            control={
              <Switch
                checked={isAdvancedOptionsEnabled}
                onChange={handleChangeAdvancedOptionsEnabled}
                color="primary"
                size="small"
              />
            }
          />
        </Stack>
      </Stack>
      {confirmationToggleOptions && (
        <ConfirmationModal
          cancelLabel="Cancel"
          open={!!confirmationToggleOptions}
          onCancel={() => setConfirmationToggleOptions(false)}
          onConfirm={() => handleConfirmationToggleOptions()}
          confirmLabel="Yes, Continue"
          title={`Confirm Change to ${
            isAdvancedOptionsEnabled ? "Basic" : "Conditional"
          } Options`}
          mainButtonType="error"
        >
          <Typography variant="body1" color="initial">
            Are you sure you want to switch to{" "}
            {isAdvancedOptionsEnabled ? "Basic" : "Conditional"} Options Mode?
            This action will result in the loss of your current options.
          </Typography>
        </ConfirmationModal>
      )}
      {confirmationToggleSplitLabel && (
        <ConfirmationModal
          cancelLabel="Cancel"
          open={!!confirmationToggleSplitLabel}
          onCancel={() => setConfirmationToggleSplitLabel(false)}
          onConfirm={() => handleConfirmationToggleSplitLabel()}
          confirmLabel="Yes, Continue"
          title="Confirm Changes"
          mainButtonType="error"
        >
          <Typography variant="body1" color="initial">
            Are you sure you want to continue? This action will result in the
            loss of your current label options.
          </Typography>
        </ConfirmationModal>
      )}
    </EntaAccordion>
  );
};

export default OptionsSection;
